import * as React from 'react';
import groundImage from '../../../../../images/ground_pattern.svg';
import { ISteppedWeirLongitudinalProps } from './ISteppedWeir';
import { getImgSrcWithColor, getTextureByMaterial, loadImage } from '../../../../../utils/graphicEngine';
import textureProtectionSystem from '../../../../../images/texture_protection_system.svg';
import { useAppDispatch } from '../../../../../../reduxService/hooks';
import { setDesignImg } from '../../../../store/project/resultsSlice';
import { roundToOne } from '../../../../../MACRA_1/components/InputForm/Geometry/utils';
import { EConversionSIToImperial, convertTo } from '../../../../../conversion';

//SETUP
const setup = {
    gabionColor: 'grey',
    borderGabionColor: 'black',
    errorColor: 'red',

    waterColor: '#83d7ee',

    borderWidth: 1,

    indicatorHeight: 30,
    indicatorWidth: 55,
    font: '11px Arial',
    colorIndicator: 'red',
    indicatorLineWidth: 1,

    counterweirWidth: 1,
    counterweirUnderSoil: 0.5,

    heightStillingBasin: 0.5,

    heightSoil: 1,
};

const SteppedWeirLongitudinal = (props: ISteppedWeirLongitudinalProps) => {
    const steppedWeirCanvas = React.useRef<HTMLCanvasElement>(null);
    let ctx: CanvasRenderingContext2D;

    let soilImageSrc: HTMLImageElement;
    let stillingBasinImageSrc: HTMLImageElement | undefined = undefined;
    let baseImageSrc: HTMLImageElement | undefined = undefined;

    let weirGeometry = { ...props.weirGeometry };
    let stillingBasin = { ...props.stillingBasin };
    let wingsGeometry = { ...props.wingsGeometry };
    const { scale, result, inspectAttribute } = props;
    const dispatch = useAppDispatch();

    let heightFromSoil = result ? Math.max(result!.hydraulicUpstream.backwaterDepth, wingsGeometry.height!) : wingsGeometry.height!;

    const getResultWidth = (): number => {
        let widthResult = 0;
        if (!!result) {
            if (result.hydraulicDownstream.hydraulicJumpLength + 0.5 > stillingBasin.poolLength! + setup.counterweirWidth) {
                widthResult = result.hydraulicDownstream.hydraulicJumpLength + 0.5 - stillingBasin.poolLength! - setup.counterweirWidth;
            }
        }
        return widthResult;
    };

    const _height = heightFromSoil + weirGeometry.stepNumber! * weirGeometry.stepHeight + setup.heightSoil + setup.indicatorHeight/scale;
    const _width =
        setup.indicatorWidth/scale +
        setup.borderWidth +
        (weirGeometry.stepHeight / weirGeometry.stepRatio!) * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) +
        weirGeometry.gabionWidth! +
        (stillingBasin.isCounterweir ? stillingBasin.poolLength! + setup.counterweirWidth : weirGeometry.gabionWidth! - weirGeometry.stepHeight / weirGeometry.stepRatio!) +
        +getResultWidth() +
        setup.borderWidth +
        setup.indicatorWidth/scale;

    inspectAttribute(_width, _height);

    React.useEffect(() => {
        let steppedWeirCanvasEle = steppedWeirCanvas.current!;
        ctx = steppedWeirCanvasEle.getContext('2d')!;
        Promise.all([loadStillingBasinImage(), loadSoilImage(), loadBaseImage()]).then(() => {
            cleanCanvas();
            if (!!result) {
                createWaterSection();
            }
            createSoil();
            createWeirGeometry();
            createStillingBasin();
            createIndicator();
            dispatch(setDesignImg(steppedWeirCanvasEle.toDataURL('image/png')));
        });
    }, [props]);

    const createWaterSection = () => {
        let z0 = result!.hydraulicUpstream.backwaterDepth;
        let zg = result!.hydraulicUpstream.criticalDepth;
        let z1 = result!.hydraulicDownstream.waterDepth;
        let z2 = result!.hydraulicDownstream.conjugateDepth;
        let z3 = result!.hydraulicDownstream.z3;
        ctx.beginPath();
        ctx.lineJoin = 'round';
        ctx.fillStyle = setup.waterColor;
        ctx.moveTo(scale * setup.indicatorWidth/scale, scale * (heightFromSoil! - z0));
        ctx.lineTo(scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry!.gabionWidth! - 1), scale * (heightFromSoil! - z0));
        ctx.lineTo(scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry!.gabionWidth!), scale * (heightFromSoil! - zg));
        ctx.lineTo(
            scale *
                (setup.indicatorWidth/scale +
                    setup.borderWidth +
                    weirGeometry!.gabionWidth! +
                    (weirGeometry.stepHeight / weirGeometry.stepRatio!) * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!)),
            scale * (heightFromSoil! + weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) - z1),
        );
        ctx.lineTo(
            scale *
                (setup.indicatorWidth/scale +
                    setup.borderWidth +
                    weirGeometry!.gabionWidth! +
                    (weirGeometry.stepHeight / weirGeometry.stepRatio!) * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) +
                    (result?.hydraulicDownstream.hydraulicJumpLength! * 2) / 3),
            scale * (heightFromSoil! + weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) - z2),
        );
        ctx.lineTo(
            scale *
                (setup.indicatorWidth/scale +
                    setup.borderWidth +
                    weirGeometry!.gabionWidth! +
                    (weirGeometry.stepHeight / weirGeometry.stepRatio!) * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) +
                    result?.hydraulicDownstream.hydraulicJumpLength!),
            scale * (heightFromSoil! + weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) - z2),
        );
        ctx.lineTo(
            scale *
                (setup.indicatorWidth/scale +
                    setup.borderWidth +
                    weirGeometry!.gabionWidth! +
                    (weirGeometry.stepHeight / weirGeometry.stepRatio!) * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) +
                    result?.hydraulicDownstream.hydraulicJumpLength! +
                    0.5),
            scale * (heightFromSoil! + weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) - z3),
        );
        ctx.lineTo(scale * (_width - setup.indicatorWidth/scale), scale * (heightFromSoil! + weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) - z3));
        ctx.lineTo(scale * (_width - setup.indicatorWidth/scale), scale * (_height - setup.indicatorHeight/scale));
        ctx.lineTo(scale * setup.indicatorWidth/scale, scale * (_height - setup.indicatorHeight/scale));
        ctx.lineTo(scale * setup.indicatorWidth/scale, scale * (heightFromSoil! - z0));
        ctx.fill();
        ctx.closePath();
    };

    const cleanCanvas = () => {
        let canvasEle = steppedWeirCanvas.current!;
        ctx = canvasEle.getContext('2d')!;
        ctx.clearRect(0, 0, canvasEle.width, canvasEle.height);
    };

    const loadStillingBasinImage = async (): Promise<void> => {
        let texture = getTextureByMaterial(stillingBasin.material!.type, stillingBasin.material!.category);
        if (!!texture) {
            let imgSrcWithColor = getImgSrcWithColor(texture, stillingBasin.material.color);
            stillingBasinImageSrc = await loadImage(imgSrcWithColor);
        }
    };

    const createIndicator = () => {
        //wings height
        createIndicatorY(heightFromSoil - wingsGeometry.height!, heightFromSoil, setup.indicatorWidth/scale / 2, wingsGeometry.height!);
        //weirGeometry height
        createIndicatorY(
            heightFromSoil,
            heightFromSoil + weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!),
            setup.indicatorWidth/scale / 2,
            weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!),
        );
        //weirGeometry foundation height
        createIndicatorY(
            heightFromSoil + weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!),
            heightFromSoil + weirGeometry.stepHeight * weirGeometry.stepNumber!,
            setup.indicatorWidth/scale / 2,
            weirGeometry.stepHeight,
        );
        //weirGeometry gabion width
        createIndicatorX(
            setup.indicatorWidth/scale + setup.borderWidth + (weirGeometry.stepHeight / weirGeometry.stepRatio!) * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!),
            setup.indicatorWidth/scale + setup.borderWidth + (weirGeometry.stepHeight / weirGeometry.stepRatio!) * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) + weirGeometry.gabionWidth!,
            _height - setup.indicatorHeight/scale / 2,
            weirGeometry.gabionWidth!,
        );
        //stilling basin length
        createIndicatorX(
            setup.indicatorWidth/scale + setup.borderWidth + (weirGeometry.stepHeight / weirGeometry.stepRatio!) * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) + weirGeometry.gabionWidth!,
            setup.indicatorWidth/scale +
                setup.borderWidth +
                (weirGeometry.stepHeight / weirGeometry.stepRatio!) * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) +
                weirGeometry.gabionWidth! +
                stillingBasin.poolLength!,
            _height - setup.indicatorHeight/scale / 2,
            stillingBasin.poolLength!,
        );
        //stilling basin counterweir
        if (stillingBasin.isCounterweir) {
            createIndicatorX(
                setup.indicatorWidth/scale +
                    setup.borderWidth +
                    (weirGeometry.stepHeight / weirGeometry.stepRatio!) * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) +
                    weirGeometry.gabionWidth! +
                    stillingBasin.poolLength!,
                setup.indicatorWidth/scale +
                    setup.borderWidth +
                    (weirGeometry.stepHeight / weirGeometry.stepRatio!) * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) +
                    weirGeometry.gabionWidth! +
                    stillingBasin.poolLength! +
                    setup.counterweirWidth,
                _height - setup.indicatorHeight/scale / 2,
                setup.counterweirWidth,
            );
            createIndicatorY(
                heightFromSoil + weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) - stillingBasin!.counterweirElevation!,
                heightFromSoil + weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!),
                _width - setup.indicatorWidth/scale / 2,
                stillingBasin.counterweirElevation!,
            );
            createIndicatorY(
                heightFromSoil + weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!),
                heightFromSoil + weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) + setup.counterweirUnderSoil,
                _width - setup.indicatorWidth/scale / 2,
                setup.counterweirUnderSoil!,
            );
        }
        //
    };

    const loadBaseImage = async (): Promise<void> => {
        let imgSrcWithColor = getImgSrcWithColor(textureProtectionSystem, setup.gabionColor);
        baseImageSrc = await loadImage(imgSrcWithColor);
    };

    const loadSoilImage = async (): Promise<void> => {
        soilImageSrc = await loadImage(groundImage);
    };

    const createSoil = () => {
        let pat = ctx.createPattern(soilImageSrc, 'repeat')!;

        ctx.beginPath();
        ctx.fillStyle = pat;
        ctx.fillRect(
            scale * setup.indicatorWidth/scale,
            scale * (heightFromSoil + weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!)),
            scale * (_width - 2 * setup.indicatorWidth/scale),
            scale * (weirGeometry.stepHeight * weirGeometry.foundationLayers! + setup.heightSoil),
        );
        ctx.closePath();
    };

    const createIndicatorY = (moveToY: number, lineToY: number, x: number, value: number) => {
        createLine([], scale * x - 2, scale * moveToY, scale * x + 2, scale * moveToY);
        createLine([], scale * x, scale * moveToY, scale * x, scale * lineToY, roundToOne(props.isImperial ? convertTo(value, EConversionSIToImperial.m_to_ft) : value));
        createLine([], scale * x - 2, scale * lineToY, scale * x + 2, scale * lineToY);
    };

    const createIndicatorX = (moveToX: number, lineToX: number, y: number, value: number) => {
        createLine([], scale * moveToX, scale * y - 2, scale * moveToX, scale * y + 2);
        createLine([], scale * moveToX, scale * y, scale * lineToX, scale * y, roundToOne(props.isImperial ? convertTo(value, EConversionSIToImperial.m_to_ft) : value));
        createLine([], scale * lineToX, scale * y - 2, scale * lineToX, scale * y + 2);
    };

    const createLine = (segments: number[], moveToX: number, moveToY: number, lineToX: number, lineToY: number, value?: number) => {
        ctx.beginPath();
        ctx.setLineDash(segments);
        ctx.moveTo(moveToX, moveToY);
        ctx.lineTo(lineToX, lineToY);
        ctx.strokeStyle = setup.colorIndicator!;
        ctx.font = setup.font;
        ctx.lineWidth = setup.indicatorLineWidth;
        ctx.lineCap = 'round';
        if (value && value > 0) {
            ctx.fillStyle = 'black';
            ctx.fillText(value.toString(), (moveToX + lineToX) / 2 + 2, (moveToY + lineToY) / 2 - 2);
        }
        ctx.stroke();
    };

    const createRectWithBorder = (x: number, y: number, w: number, h: number, pat: string | CanvasPattern | CanvasGradient) => {
        ctx.fillStyle = pat;
        ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = setup.borderGabionColor;
        ctx.strokeRect(x, y, w, h);
    };

    const handleGabionOverHeight = (x: number, y: number, w: number, h: number, colorGabion?: string | CanvasPattern | CanvasGradient) => {
        if (h > 1) {
            for (let i = 1; i <= h; i++) {
                colorGabion ? createRectWithBorder(scale * x, scale * (y + h - i), scale * w, scale * 1, colorGabion) : ctx.strokeRect(scale * x, scale * (y + h - i), scale * w, scale * 1);
            }
            if (h - Math.trunc(h) > 0) {
                colorGabion
                    ? createRectWithBorder(scale * x, scale * y, scale * w, scale * (h - Math.trunc(h)), colorGabion)
                    : ctx.strokeRect(scale * x, scale * y, scale * w, scale * (h - Math.trunc(h)));
            }
        } else {
            colorGabion ? createRectWithBorder(scale * x, scale * y, scale * w, scale * h, colorGabion) : ctx.strokeRect(scale * x, scale * y, scale * w, scale * h);
        }
    };

    const createWeirGeometry = () => {
        let offsetForBugFix = 1; // without it show a white line;
        let pat = ctx.createPattern(soilImageSrc, 'repeat')!;
        let basePat = ctx.createPattern(baseImageSrc!, 'repeat');

        for (let i = 0; i < weirGeometry.stepNumber!; i++) {
            //anchor
            ctx.fillStyle = pat;
            ctx.fillRect(
                scale * setup.indicatorWidth/scale,
                scale * (heightFromSoil + i * weirGeometry.stepHeight),
                scale *
                    (setup.borderWidth +
                        (weirGeometry.stepHeight / weirGeometry.stepRatio!) *
                            (i < weirGeometry.stepNumber! - weirGeometry.foundationLayers! ? i : weirGeometry.stepNumber! - weirGeometry.foundationLayers!)),
                scale * weirGeometry.stepHeight + offsetForBugFix,
            );
            //gabions
            createRectWithBorder(
                scale *
                    (setup.indicatorWidth/scale +
                        setup.borderWidth +
                        (weirGeometry.stepHeight / weirGeometry.stepRatio!) *
                            (i < weirGeometry.stepNumber! - weirGeometry.foundationLayers! ? i : weirGeometry.stepNumber! - weirGeometry.foundationLayers!)),
                scale * (heightFromSoil + +i * weirGeometry.stepHeight),
                scale * weirGeometry.gabionWidth!,
                scale * weirGeometry.stepHeight,
                basePat!,
            );
        }

        //wings
        ctx.save();
        ctx.beginPath();
        ctx.setLineDash([5, 5]);
        ctx.moveTo(scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.gabionWidth! - weirGeometry.stepHeight / weirGeometry.stepRatio!), scale * heightFromSoil);
        ctx.lineTo(
            scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.gabionWidth! - weirGeometry.stepHeight / weirGeometry.stepRatio!),
            scale * (heightFromSoil - wingsGeometry.height!),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.gabionWidth! - weirGeometry.stepHeight / weirGeometry.stepRatio! + weirGeometry.gabionWidth!),
            scale * (heightFromSoil - wingsGeometry.height!),
        );
        for (let i = 0; i < weirGeometry.stepNumber! - weirGeometry.foundationLayers!; i++) {
            ctx.lineTo(
                scale *
                    (setup.indicatorWidth/scale +
                        setup.borderWidth +
                        weirGeometry.gabionWidth! -
                        weirGeometry.stepHeight / weirGeometry.stepRatio! +
                        weirGeometry.gabionWidth! +
                        i * (weirGeometry.stepHeight / weirGeometry.stepRatio!)),
                scale * (heightFromSoil - wingsGeometry.height! + (i + 1) * weirGeometry.stepHeight),
            );
            ctx.lineTo(
                scale *
                    (setup.indicatorWidth/scale +
                        setup.borderWidth +
                        weirGeometry.gabionWidth! -
                        weirGeometry.stepHeight / weirGeometry.stepRatio! +
                        weirGeometry.gabionWidth! +
                        (i + 1) * (weirGeometry.stepHeight / weirGeometry.stepRatio!)),
                scale * (heightFromSoil - wingsGeometry.height! + (i + 1) * weirGeometry.stepHeight),
            );
            if (i == weirGeometry.stepNumber! - weirGeometry.foundationLayers! - 1) {
                if (!stillingBasin.isCounterweir) {
                    ctx.lineTo(
                        scale *
                            (setup.indicatorWidth/scale +
                                setup.borderWidth +
                                weirGeometry.gabionWidth! -
                                weirGeometry.stepHeight / weirGeometry.stepRatio! +
                                weirGeometry.gabionWidth! +
                                ((i + 1) * weirGeometry.stepHeight) / weirGeometry.stepRatio!),
                        scale * (heightFromSoil - wingsGeometry.height! + (i + 1) * weirGeometry.stepHeight),
                    );
                } else {
                    ctx.lineTo(
                        scale *
                            (setup.indicatorWidth/scale +
                                setup.borderWidth +
                                (weirGeometry.stepHeight / weirGeometry.stepRatio!) * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) +
                                weirGeometry.gabionWidth! +
                                stillingBasin.poolLength! +
                                setup.counterweirWidth),
                        scale * (heightFromSoil - wingsGeometry.height! + (i + 1) * weirGeometry.stepHeight),
                    );
                    ctx.lineTo(
                        scale *
                            (setup.indicatorWidth/scale +
                                setup.borderWidth +
                                (weirGeometry.stepHeight / weirGeometry.stepRatio!) * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) +
                                weirGeometry.gabionWidth! +
                                stillingBasin.poolLength! +
                                setup.counterweirWidth),
                        scale * (heightFromSoil + (i + 1) * weirGeometry.stepHeight),
                    );
                }
            }
        }

        ctx.stroke();
        ctx.closePath();
        ctx.restore();
    };

    const createStillingBasin = () => {
        if (stillingBasin.isCounterweir) {
            let isThereCounterweirElevationError_Macra2_Stepped = stillingBasin?.counterweirElevation! < result?.basinDesign?.minCounterweirHeight!;
            let basePat = ctx.createPattern(baseImageSrc!, 'repeat');
            handleGabionOverHeight(
                setup.indicatorWidth/scale +
                    setup.borderWidth +
                    (weirGeometry.stepHeight / weirGeometry.stepRatio!) * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) +
                    weirGeometry.gabionWidth! +
                    stillingBasin.poolLength!,
                heightFromSoil + weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) - stillingBasin!.counterweirElevation!,
                setup.counterweirWidth,
                stillingBasin.counterweirElevation! + setup.counterweirUnderSoil,
                isThereCounterweirElevationError_Macra2_Stepped ? setup.errorColor : basePat!,
            );
        }

        if (stillingBasin.isStillingBasin) {
            let isThereStillingBasinWidthError_Macra2_Stepped = stillingBasin?.poolLength! < result?.basinDesign?.minLengthOfTheStillingPool!;
            let pat = !!stillingBasinImageSrc ? ctx.createPattern(stillingBasinImageSrc, 'repeat') : stillingBasin.material.color;

            createRectWithBorder(
                scale *
                    (setup.indicatorWidth/scale +
                        setup.borderWidth +
                        (weirGeometry.stepHeight / weirGeometry.stepRatio!) * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) +
                        weirGeometry.gabionWidth!),
                scale * (heightFromSoil + weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!)),
                scale * stillingBasin.poolLength!,
                scale * setup.heightStillingBasin,
                isThereStillingBasinWidthError_Macra2_Stepped ? setup.errorColor : pat!,
            );
        }
    };

    return (
        <div className='position-absolute'>
            <canvas width={scale * _width} height={scale * _height} ref={steppedWeirCanvas}></canvas>
        </div>
    );
};

export default SteppedWeirLongitudinal;
